/**
 * «ثبت‌های این محصول» — یک داده، سه نگاه.
 *
 * قبلاً فقط یک لیستِ تخت بود که هر ردیفش «پرسنل × عملیات × سایز» را تجمیع
 * می‌کرد. برای سؤالِ واقعیِ سرِ ماه («فریبا روی این محصول چقدر کار کرده؟» یا
 * «زیگزال را کی‌ها زده‌اند؟») باید چشمی جمع می‌زدی.
 *
 * حالا:
 *   • **پرسنل** → هر نفر یک ردیف با جمعِ کلش؛ باز که شود عملیات‌هایش.
 *   • **عملیات** → هر عملیات یک ردیف؛ باز که شود چه کسانی و چقدر.
 *   • **جدولی** → همان شکلِ برگهٔ ثبتِ کارکرد: سطر=پرسنل، ستون=عملیات.
 *
 * در هر سه حالت، برگ‌ها همان ثبت‌های تکیِ قابلِ ویرایش/حذف‌اند — دادهٔ اصلی
 * دست‌نخورده می‌ماند و اینجا فقط جور دیگری چیده می‌شود.
 */
import React, { useMemo, useState } from "react";
import { Button, Modal, Popconfirm, Segmented, Select, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import Alert from "../../components/GxAlert";
import {
  CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined,
} from "@ant-design/icons";
import GxTable from "../../components/GxTable";
import { loadOrder, saveOrder } from "../../utils/layoutStore";
import FormulaNumber from "../../components/FormulaNumber";
import { fmtNum, toJalali } from "../../utils/dateUtils";
import { formatMoney, shortOpLabel } from "../../utils/money";

const { Text } = Typography;

const amountOf = (e) => Number(e.line_total)
  || ((Number(e.unit_price) || 0) * (Number(e.quantity) || 0));

const opLabelOf = (e) => shortOpLabel(e.operation_name, e.stitch_lines);

/** درختِ دوسطحی + برگ‌ها. `first`/`second` تعیین می‌کنند کدام بالا بنشیند. */
export function buildTree(entries, first, second) {
  const keyOf = { person: (e) => `p${e.employee_id}`, op: (e) => `o${e.catalog_id}` };
  const labelOf = { person: (e) => e.employee_name, op: opLabelOf };

  const top = new Map();
  (entries || []).forEach((e) => {
    const tk = keyOf[first](e);
    if (!top.has(tk)) {
      top.set(tk, {
        key: tk, label: labelOf[first](e), quantity: 0, amount: 0, count: 0,
        _seq: e.id || 0, _sub: new Map(),
      });
    }
    const t = top.get(tk);
    t.quantity += Number(e.quantity) || 0;
    t.amount += amountOf(e);
    t.count += 1;

    // سطحِ دوم per سایز جدا می‌ماند: «زیگزال 2XL» و «زیگزال 3XL» یک کار نیستند
    const sk = `${tk}-${keyOf[second](e)}-${e.size_label || ""}`;
    if (!t._sub.has(sk)) {
      t._sub.set(sk, {
        key: sk, label: labelOf[second](e), size_label: e.size_label,
        quantity: 0, amount: 0, count: 0, warning: false, _seq: e.id || 0, children: [],
      });
    }
    const g = t._sub.get(sk);
    g.quantity += Number(e.quantity) || 0;
    g.amount += amountOf(e);
    g.count += 1;
    if (e.warning) g.warning = true;
    g.children.push({
      ...e, key: `e${e.id}`, label: toJalali(e.entry_date),
      amount: amountOf(e), _leaf: true,
    });
  });

  return [...top.values()].map((t) => ({
    ...t,
    children: [...t._sub.values()].map((g) => ({
      ...g,
      // ثبتِ تکی: سطحِ سومِ اضافه فقط اذیت می‌کند
      children: g.children.length > 1 ? g.children : undefined,
      ...(g.children.length === 1 ? { _entry: g.children[0] } : {}),
    })),
    _sub: undefined,
    // ترتیبِ ورود، نه ترتیبِ تعداد — کنترل‌چی برگه را از بالا به پایین می‌خواند
  })).sort((a, b) => a._seq - b._seq);
}

/** سطر=پرسنل، ستون=عملیات — همان شکلِ برگهٔ ثبتِ کارکرد. */
export function buildPivot(entries) {
  const ops = new Map();
  const people = new Map();
  (entries || []).forEach((e) => {
    const ok = String(e.catalog_id);
    if (!ops.has(ok)) ops.set(ok, { key: ok, label: opLabelOf(e), total: 0, _seq: e.id || 0 });
    ops.get(ok).total += Number(e.quantity) || 0;

    const pk = String(e.employee_id);
    if (!people.has(pk)) {
      people.set(pk, {
        key: pk, employee_name: e.employee_name, cells: {}, total: 0, amount: 0,
        _seq: e.id || 0,
      });
    }
    const p = people.get(pk);
    p.cells[ok] = (p.cells[ok] || 0) + (Number(e.quantity) || 0);
    p.total += Number(e.quantity) || 0;
    p.amount += amountOf(e);
  });
  return {
    ops: [...ops.values()].sort((a, b) => a._seq - b._seq),
    rows: [...people.values()].sort((a, b) => a._seq - b._seq),
  };
}

/** همهٔ ثبت‌های تکیِ زیرِ یک ردیفِ درخت (هر عمقی) */
function leavesOf(row) {
  if (row._leaf) return [row];
  if (row._entry) return [row._entry];
  return (row.children || []).flatMap(leavesOf);
}

export default function EntriesTable({
  entries, money, layoutId, locked, employees = [],
  onEdit, onDelete, onSaveEntry, onBulk,
}) {
  // نمای «ثبت‌های این محصول» per کاربر یادش می‌ماند (خواستهٔ کاربر ۱۴۰۵/۰۶/۲۲:
  // «روی همون مدلی که تعیین می‌کنیم بمونه») — یک انتخاب برای همهٔ محصول‌ها.
  const [mode, setModeState] = useState(() => {
    const v = loadOrder("pick", "entries-mode");
    return ["person", "op", "pivot"].includes(v) ? v : "person";
  });
  const setMode = (v) => { saveOrder("pick", "entries-mode", v); setModeState(v); };
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkField, setBulkField] = useState("employee_id");
  const [bulkValue, setBulkValue] = useState(null);
  const [busy, setBusy] = useState(false);
  // ویرایشِ درجا: فقط یک ردیف هم‌زمان، تا اشتباهی چند جا عوض نشود
  const [draft, setDraft] = useState(null);

  const tree = useMemo(() => (mode === "pivot" ? [] : buildTree(
    entries, mode === "person" ? "person" : "op", mode === "person" ? "op" : "person")),
  [entries, mode]);
  const pivot = useMemo(() => (mode === "pivot" ? buildPivot(entries) : null), [entries, mode]);

  const entryOf = (r) => (r._leaf ? r : r._entry) || null;

  const saveDraft = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await onSaveEntry(draft.id, {
        quantity: Number(draft.quantity) || 0,
        employee_id: draft.employee_id,
        notes: draft.notes ?? null,
      });
      setDraft(null);
    } catch (e) {
      message.error(e?.response?.data?.detail?.message
        || e?.response?.data?.detail || "ذخیره نشد");
    }
    setBusy(false);
  };

  const actions = (r) => {
    const e = entryOf(r);
    if (!e) return <Text type="secondary" style={{ fontSize: 11 }}>باز کنید</Text>;
    if (locked) return <Tag color="purple">قفل</Tag>;
    if (draft?.id === e.id) {
      return (
        <Space size={2}>
          <Button size="small" type="primary" loading={busy}
            icon={<CheckOutlined />} onClick={saveDraft} />
          <Button size="small" icon={<CloseOutlined />} onClick={() => setDraft(null)} />
        </Space>
      );
    }
    return (
      <Space size={2}>
        {/* ویرایشِ درجا — مودال فقط وقتی لازم است که فیلدِ دیگری بخواهد */}
        <Tooltip title="ویرایش در همین جدول">
          <Button size="small" icon={<EditOutlined />}
            onClick={() => setDraft({
              id: e.id, quantity: e.quantity,
              employee_id: e.employee_id, notes: e.notes || "",
            })} />
        </Tooltip>
        <Tooltip title="ویرایشِ کامل (تاریخ، عملیات…)">
          <Button size="small" onClick={() => onEdit(e)}>…</Button>
        </Tooltip>
        <Popconfirm title="حذف این ثبت؟ رکورد حقوقش هم پاک می‌شود"
          okText="بله" cancelText="خیر"
          onConfirm={async () => { await onDelete(e); message.success("حذف شد"); }}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    );
  };

  /** برگ‌های انتخاب‌شده — انتخابِ یک گروه یعنی همهٔ ثبت‌های زیرش */
  const selectedEntries = useMemo(() => {
    const wanted = new Set(selected);
    const out = new Map();
    const walk = (rows) => (rows || []).forEach((r) => {
      if (wanted.has(r.key)) leavesOf(r).forEach((e) => out.set(e.id, e));
      walk(r.children);
    });
    walk(tree);
    return [...out.values()];
  }, [selected, tree]);

  const applyBulk = async () => {
    if (!selectedEntries.length) return message.warning("ردیفی انتخاب نشده");
    if (bulkValue == null || bulkValue === "") return message.warning("مقدار را وارد کنید");
    setBusy(true);
    try {
      await onBulk(selectedEntries, { [bulkField]: bulkField === "quantity"
        ? Number(bulkValue) : bulkValue });
      setBulkOpen(false);
      setSelected([]);
      setBulkValue(null);
    } catch (e) {
      message.error(e?.response?.data?.detail?.message
        || e?.response?.data?.detail || "ویرایشِ دسته‌جمعی ناموفق بود");
    }
    setBusy(false);
    return undefined;
  };

  const treeColumns = [
    {
      key: "label", title: mode === "person" ? "پرسنل / عملیات" : "عملیات / پرسنل",
      dataIndex: "label", width: 240,
      sorter: (a, b) => String(a.label || "").localeCompare(String(b.label || "")),
      render: (v, r) => {
        const e = entryOf(r);
        // در حالِ ویرایش: روی برگ می‌شود پرسنل را هم عوض کرد
        if (e && draft?.id === e.id && mode === "op") {
          return (
            <Select size="small" showSearch style={{ width: "100%" }} options={employees}
              optionFilterProp="label" value={draft.employee_id}
              onChange={(x) => setDraft((d) => ({ ...d, employee_id: x }))} />
          );
        }
        return (
          <Space size={4}>
            <span style={{ fontWeight: r.children ? 600 : 400 }}>{v}</span>
            {r.warning && <Tooltip title="تعداد خط یا نرخ نیاز به بررسی دارد"><Tag color="gold">هشدار</Tag></Tooltip>}
          </Space>
        );
      },
    },
    {
      key: "qty", title: "تعداد کل", dataIndex: "quantity", width: 110, align: "center",
      sorter: (a, b) => (a.quantity || 0) - (b.quantity || 0),
      render: (v, r) => {
        const e = entryOf(r);
        if (e && draft?.id === e.id) {
          return (
            <FormulaNumber size="small" min={1} value={draft.quantity}
              onChange={(x) => setDraft((d) => ({ ...d, quantity: x }))} />
          );
        }
        return <b>{fmtNum(v)}</b>;
      },
    },
    ...(!money ? [] : [{
      key: "amount", title: "مبلغ کل (ریال)", dataIndex: "amount", width: 140, align: "left",
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
      render: (v) => <b className="gx-money">{formatMoney(v)}</b>,
    }]),
    { key: "count", title: "دفعات", dataIndex: "count", width: 80, align: "center",
      sorter: (a, b) => (a.count || 0) - (b.count || 0),
      render: (v) => (v > 1 ? <Tag color="blue">{fmtNum(v)}</Tag> : fmtNum(v)) },
    {
      key: "notes", title: "توضیح", dataIndex: "notes", width: 160,
      render: (v, r) => {
        const e = entryOf(r);
        if (e && draft?.id === e.id) {
          return (
            <Select size="small" mode="tags" maxTagCount={1} style={{ width: "100%" }}
              value={draft.notes ? [draft.notes] : []} options={[]}
              onChange={(x) => setDraft((d) => ({ ...d, notes: x[x.length - 1] || "" }))} />
          );
        }
        return v || "—";
      },
    },
    { key: "act", title: "", width: 90, render: (_, r) => actions(r) },
  ];

  const pivotColumns = pivot ? [
    { key: "emp", title: "پرسنل", dataIndex: "employee_name", width: 180,
      sorter: (a, b) => String(a.employee_name).localeCompare(String(b.employee_name)) },
    ...pivot.ops.map((op) => ({
      key: `op${op.key}`, title: op.label, width: 130, align: "center",
      sorter: (a, b) => (a.cells[op.key] || 0) - (b.cells[op.key] || 0),
      render: (_, r) => (r.cells[op.key]
        ? fmtNum(r.cells[op.key])
        : <span style={{ color: "#ddd" }}>—</span>),
    })),
    { key: "total", title: "جمع", dataIndex: "total", width: 100, align: "center",
      sorter: (a, b) => a.total - b.total,
      render: (v) => <b>{fmtNum(v)}</b> },
    ...(!money ? [] : [{
      key: "amount", title: "مبلغ (ریال)", dataIndex: "amount", width: 140, align: "left",
      sorter: (a, b) => a.amount - b.amount,
      render: (v) => <b className="gx-money">{formatMoney(v)}</b>,
    }]),
  ] : [];

  const bulkModal = (
    <Modal open={bulkOpen} title="ویرایشِ دسته‌جمعی" okText="اعمال" cancelText="انصراف"
      confirmLoading={busy} onCancel={() => setBulkOpen(false)} onOk={applyBulk}>
      <Alert type="warning" showIcon style={{ marginBottom: 12 }}
        message={`${fmtNum(selectedEntries.length)} ثبت تغییر می‌کند`}
        description="رکوردِ حقوقیِ هرکدام هم همان لحظه به‌روز می‌شود." />
      <Space direction="vertical" style={{ width: "100%" }} size={8}>
        <Select style={{ width: "100%" }} value={bulkField}
          onChange={(v) => { setBulkField(v); setBulkValue(null); }}
          options={[
            { value: "employee_id", label: "پرسنل" },
            { value: "quantity", label: "تعداد" },
            { value: "notes", label: "توضیح" },
          ]} />
        {bulkField === "employee_id" && (
          <Select showSearch style={{ width: "100%" }} placeholder="پرسنلِ جدید"
            optionFilterProp="label" options={employees}
            value={bulkValue} onChange={setBulkValue} />
        )}
        {bulkField === "quantity" && (
          <FormulaNumber min={1} placeholder="تعدادِ جدید (می‌تواند جمع باشد)"
            value={bulkValue} onChange={setBulkValue} />
        )}
        {bulkField === "notes" && (
          <Select mode="tags" maxTagCount={1} style={{ width: "100%" }} options={[]}
            placeholder="توضیحِ جدید" value={bulkValue ? [bulkValue] : []}
            onChange={(x) => setBulkValue(x[x.length - 1] || "")} />
        )}
      </Space>
    </Modal>
  );

  const bulkBar = (!locked && selectedEntries.length > 0 && mode !== "pivot") ? (
    <Space style={{ marginBottom: 8 }} wrap>
      <Text strong style={{ fontSize: 12 }}>
        {fmtNum(selectedEntries.length)} ثبت انتخاب شده
      </Text>
      <Button size="small" type="primary" icon={<EditOutlined />}
        onClick={() => setBulkOpen(true)}>ویرایشِ دسته‌جمعی</Button>
      <Popconfirm title={`حذفِ ${fmtNum(selectedEntries.length)} ثبت؟ رکوردهای حقوقشان هم پاک می‌شود`}
        okText="بله، حذف کن" cancelText="خیر"
        onConfirm={async () => {
          setBusy(true);
          try {
            for (const e of selectedEntries) await onDelete(e);
            setSelected([]);
          } finally { setBusy(false); }
        }}>
        <Button size="small" danger icon={<DeleteOutlined />} loading={busy}>حذفِ دسته‌جمعی</Button>
      </Popconfirm>
      <Button size="small" onClick={() => setSelected([])}>لغوِ انتخاب</Button>
    </Space>
  ) : null;

  const header = (
    <Space style={{ marginBottom: 8, width: "100%", justifyContent: "space-between" }} wrap>
      <Text strong style={{ fontSize: 12 }}>ثبت‌های این محصول</Text>
      <Segmented size="small" value={mode} onChange={setMode}
        options={[
          { value: "person", label: "بر اساس پرسنل" },
          { value: "op", label: "بر اساس عملیات" },
          { value: "pivot", label: "جدولی" },
        ]} />
    </Space>
  );

  if (!entries?.length) {
    return <div style={{ marginTop: 10 }}>{header}
      <Text type="secondary" style={{ fontSize: 12 }}>هنوز کارکردی ثبت نشده است.</Text></div>;
  }

  return (
    <div style={{ marginTop: 10 }}>
      {header}
      {bulkBar}
      {bulkModal}
      {locked && (
        <Alert type="info" showIcon style={{ marginBottom: 8 }}
          message="کنترل اولِ این سایز ثبت شده — ویرایش بسته است"
          description="برای اصلاح، از بالای همین تب «باز کردن برای اصلاح» را بزنید." />
      )}
      {mode === "pivot" ? (
        <GxTable size="small" rowKey="key" pagination={false} gxExport={false}
          layoutId={`${layoutId}-pivot`} dataSource={pivot.rows} columns={pivotColumns}
          scroll={{ x: "max-content" }}
          summary={() => (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><b>جمع</b></Table.Summary.Cell>
                {pivot.ops.map((op, i) => (
                  <Table.Summary.Cell key={op.key} index={i + 1} align="center">
                    <b>{fmtNum(op.total)}</b>
                  </Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={pivot.ops.length + 1} align="center">
                  <b>{fmtNum(pivot.rows.reduce((a, r) => a + r.total, 0))}</b>
                </Table.Summary.Cell>
                {money && (
                  <Table.Summary.Cell index={pivot.ops.length + 2} align="left">
                    <b className="gx-money">
                      {formatMoney(pivot.rows.reduce((a, r) => a + r.amount, 0))}
                    </b>
                  </Table.Summary.Cell>
                )}
              </Table.Summary.Row>
            </Table.Summary>
          )} />
      ) : (
        <GxTable size="small" rowKey="key" pagination={false} gxExport={false}
          layoutId={`${layoutId}-${mode}`} dataSource={tree} columns={treeColumns}
          scroll={{ x: "max-content" }}
          rowClassName={(r) => (r.warning ? "gx-op-warn" : "")}
          rowSelection={locked ? undefined : {
            selectedRowKeys: selected,
            onChange: setSelected,
            checkStrictly: false,
          }}
          expandable={{ defaultExpandAllRows: false }} />
      )}
    </div>
  );
}
